import { Switch } from 'react-native';
import { HStack, Text, VStack } from '../../../components/primitives';
import { colors } from '../../../theme/colors';

export interface LeadInviteProtectionStatus {
  doNotCall: boolean;
  requiresReview: boolean;
}

interface LeadInviteToggleProps {
  /** Organization.leadInviteEnabled === true — never treat null/false as on. */
  orgInviteEnabled: boolean;
  /** null when the event has no lead attached. */
  leadProtection: LeadInviteProtectionStatus | null;
  checked: boolean;
  onToggle: (next: boolean) => void;
}

type DisabledReason = 'do_not_call' | 'requires_review';

// Same wording used for calendar.invite_sent's blocked_consent case (see
// LeadInviteToggle's sibling reason map further down and
// lead-invite-predicate.ts on the backend) — a rep should never see
// 'do_not_call' as a raw string.
const DISABLED_REASON_TEXT: Record<DisabledReason, string> = {
  do_not_call:
    'This lead has opted out of contact (do-not-call). The invite stays off to respect that.',
  requires_review:
    'This lead requires manual review before being contacted. The invite is disabled until then.',
};

function resolveDisabledReason(
  leadProtection: LeadInviteProtectionStatus | null,
): DisabledReason | null {
  if (leadProtection?.doNotCall) return 'do_not_call';
  if (leadProtection?.requiresReview) return 'requires_review';
  return null;
}

// The one place that decides whether a rep can even ask to invite a lead.
// Mirrors the backend's resolveLeadInviteDecision in spirit — fail closed,
// state the reason, never a bare disabled control — but this component only
// governs what is offered, never sends anything itself.
//
// Hidden (not merely disabled) when the org never opted in: a control that
// exists but never works implies a capability the org does not have.
export function LeadInviteToggle({
  orgInviteEnabled,
  leadProtection,
  checked,
  onToggle,
}: Readonly<LeadInviteToggleProps>) {
  if (!orgInviteEnabled) return null;

  const disabledReason = resolveDisabledReason(leadProtection);
  const disabled = disabledReason !== null;
  const reasonText = disabledReason ? DISABLED_REASON_TEXT[disabledReason] : undefined;

  return (
    <VStack spacing="xxs">
      <HStack spacing="md" align="center" justify="between">
        <Text variant="body" color={colors.text.primary}>
          Invite lead to this meeting
        </Text>
        <Switch
          testID="lead_invite_toggle"
          value={checked}
          onValueChange={onToggle}
          disabled={disabled}
          accessibilityLabel="Invite lead to this meeting"
          accessibilityHint={reasonText}
          accessibilityState={{ disabled, checked }}
          trackColor={{ false: colors.border.default, true: colors.primary[500] }}
          thumbColor={colors.background.primary}
        />
      </HStack>
      {reasonText && (
        <Text testID="lead_invite_toggle_reason" variant="caption" color={colors.text.tertiary}>
          {reasonText}
        </Text>
      )}
    </VStack>
  );
}
